import { supabase } from '../lib/supabase';

/**
 * Monitoring Service
 * Handles real-time monitoring of workflow jobs with metrics calculation
 */

export const monitoringService = {
  /**
   * Subscribe to workflow job updates in real-time
   */
  subscribeToWorkflowJobs(userId, onJobUpdate) {
    if (!userId || !supabase) return null;

    const channel = supabase?.channel('workflow-jobs-monitoring')?.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'workflow_jobs',
        filter: `user_id=eq.${userId}`
      },
      (payload) => {
        if (payload?.eventType === 'INSERT') {
          onJobUpdate?.('INSERT', payload?.new);
        } else if (payload?.eventType === 'UPDATE') {
          onJobUpdate?.('UPDATE', payload?.new);
        } else if (payload?.eventType === 'DELETE') {
          onJobUpdate?.('DELETE', payload?.old);
        }
      }
    )?.subscribe();

    return channel;
  },

  /**
   * Unsubscribe from workflow job updates
   */
  unsubscribeFromWorkflowJobs(channel) {
    if (channel && supabase) {
      supabase?.removeChannel(channel);
    }
  },

  /**
   * Get monitoring dashboard metrics
   */
  async getDashboardMetrics() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: jobs, error } = await supabase?.from('workflow_jobs')?.select('*')?.eq('user_id', user?.id);

      if (error) throw error;

      // Calculate metrics
      const totalJobs = jobs?.length || 0;
      const completedJobs = jobs?.filter(j => j?.status === 'completed')?.length || 0;
      const failedJobs = jobs?.filter(j => j?.status === 'failed')?.length || 0;
      const runningJobs = jobs?.filter(j => j?.status === 'running')?.length || 0;
      const pendingJobs = jobs?.filter(j => j?.status === 'pending')?.length || 0;

      // Success rate calculation
      const successRate = totalJobs > 0 ? ((completedJobs / totalJobs) * 100)?.toFixed(1) : 0;

      // Average processing time (in minutes)
      const completedJobsWithTime = jobs?.filter(j => j?.status === 'completed' && j?.started_at && j?.completed_at);
      const totalProcessingTime = completedJobsWithTime?.reduce((sum, job) => {
        const start = new Date(job?.started_at);
        const end = new Date(job?.completed_at);
        return sum + (end - start);
      }, 0);
      const avgProcessingTime = completedJobsWithTime?.length > 0 
        ? Math.round(totalProcessingTime / completedJobsWithTime?.length / 60000) 
        : 0;

      // Cost savings calculation (from results JSONB)
      const totalCostSavings = jobs?.reduce((sum, job) => {
        const savings = job?.results?.costSavings || job?.results?.timeSaved || 0;
        return sum + (typeof savings === 'number' ? savings : 0);
      }, 0);

      // Total items processed
      const totalItemsProcessed = jobs?.reduce((sum, job) => sum + (job?.processed_items || 0), 0);
      const totalItemsFailed = jobs?.reduce((sum, job) => sum + (job?.failed_items || 0), 0);

      return {
        totalJobs,
        completedJobs,
        failedJobs,
        runningJobs,
        pendingJobs,
        successRate: parseFloat(successRate),
        avgProcessingTime,
        totalCostSavings,
        totalItemsProcessed,
        totalItemsFailed
      };
    } catch (error) {
      console.error('Error fetching dashboard metrics:', error);
      throw error;
    }
  },

  /**
   * Get processing time trends (last 7 days)
   */
  async getProcessingTimeTrends() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const sevenDaysAgo = new Date();
      sevenDaysAgo?.setDate(sevenDaysAgo?.getDate() - 7);

      const { data: jobs, error } = await supabase?.from('workflow_jobs')?.select('*')?.eq('user_id', user?.id)?.eq('status', 'completed')?.gte('completed_at', sevenDaysAgo?.toISOString())?.order('completed_at', { ascending: true });

      if (error) throw error;

      // Group by date
      const trendsByDate = {};
      jobs?.forEach(job => {
        if (job?.started_at && job?.completed_at) {
          const date = new Date(job?.completed_at)?.toLocaleDateString('ar-JO');
          const processingTime = (new Date(job?.completed_at) - new Date(job?.started_at)) / 60000; // minutes

          if (!trendsByDate?.[date]) {
            trendsByDate[date] = { total: 0, count: 0 };
          }
          trendsByDate[date].total += processingTime;
          trendsByDate[date].count += 1;
        }
      });

      // Calculate averages
      const trends = Object.keys(trendsByDate)?.map(date => ({
        date,
        avgTime: Math.round(trendsByDate?.[date]?.total / trendsByDate?.[date]?.count)
      }));

      return trends;
    } catch (error) {
      console.error('Error fetching processing time trends:', error);
      throw error;
    }
  },

  /**
   * Get success rate by job type
   */
  async getSuccessRateByJobType() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: jobs, error } = await supabase?.from('workflow_jobs')?.select('*')?.eq('user_id', user?.id);

      if (error) throw error;

      // Group by job type
      const jobTypeStats = {};
      jobs?.forEach(job => {
        const type = job?.job_type;
        if (!jobTypeStats?.[type]) {
          jobTypeStats[type] = { total: 0, completed: 0, failed: 0 };
        }
        jobTypeStats[type].total += 1;
        if (job?.status === 'completed') jobTypeStats[type].completed += 1;
        if (job?.status === 'failed') jobTypeStats[type].failed += 1;
      });

      // Calculate success rates
      const successRates = Object.keys(jobTypeStats)?.map(type => {
        const stats = jobTypeStats?.[type];
        const successRate = stats?.total > 0 ? ((stats?.completed / stats?.total) * 100)?.toFixed(1) : 0;
        return {
          jobType: type,
          successRate: parseFloat(successRate),
          total: stats?.total,
          completed: stats?.completed,
          failed: stats?.failed
        };
      });

      return successRates;
    } catch (error) {
      console.error('Error fetching success rate by job type:', error);
      throw error;
    }
  },

  /**
   * Get failure analysis
   */
  async getFailureAnalysis() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: jobs, error } = await supabase?.from('workflow_jobs')?.select('*')?.eq('user_id', user?.id)?.eq('status', 'failed')?.order('created_at', { ascending: false })?.limit(50);

      if (error) throw error;

      // Parse error logs and categorize failures
      const failureCategories = {};
      const recentFailures = [];

      jobs?.forEach(job => {
        const errorLog = job?.error_log || 'خطأ غير محدد';
        
        // Categorize errors
        let category = 'أخرى';
        if (errorLog?.includes('API') || errorLog?.includes('quota')) {
          category = 'أخطاء API';
        } else if (errorLog?.includes('network') || errorLog?.includes('timeout')) {
          category = 'أخطاء الشبكة';
        } else if (errorLog?.includes('authentication') || errorLog?.includes('permission')) {
          category = 'أخطاء الصلاحيات';
        } else if (errorLog?.includes('validation') || errorLog?.includes('invalid')) {
          category = 'أخطاء التحقق';
        }

        failureCategories[category] = (failureCategories?.[category] || 0) + 1;

        // Add to recent failures
        recentFailures?.push({
          id: job?.id,
          jobName: job?.job_name,
          jobType: job?.job_type,
          errorLog: errorLog?.substring(0, 200),
          failedItems: job?.failed_items,
          createdAt: job?.created_at
        });
      });

      // Convert to array for pie chart
      const failureDistribution = Object.keys(failureCategories)?.map(category => ({
        category,
        count: failureCategories?.[category]
      }));

      return {
        failureDistribution,
        recentFailures: recentFailures?.slice(0, 10),
        totalFailures: jobs?.length
      };
    } catch (error) {
      console.error('Error fetching failure analysis:', error);
      throw error;
    }
  },

  /**
   * Get recent job activity
   */
  async getRecentJobActivity(limit = 20) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: jobs, error } = await supabase?.from('workflow_jobs')?.select('*')?.eq('user_id', user?.id)?.order('created_at', { ascending: false })?.limit(limit);

      if (error) throw error;

      return jobs?.map(job => ({
        id: job?.id,
        jobType: job?.job_type,
        jobName: job?.job_name,
        status: job?.status,
        totalItems: job?.total_items,
        processedItems: job?.processed_items,
        failedItems: job?.failed_items,
        startedAt: job?.started_at,
        completedAt: job?.completed_at,
        createdAt: job?.created_at
      }));
    } catch (error) {
      console.error('Error fetching recent job activity:', error);
      throw error;
    }
  }
};
